import { fetchEventSource } from '@microsoft/fetch-event-source';

import { accessToken } from '@/configuration/tokenHandling/accessToken';
import { AccessToken } from '@/configuration/tokenHandling/tokenSlice';
import Logger from '@/utils/logger';

type StatusEventResponse = {
  type: string | undefined;
  event_id: string;
  message: string;
  thread_id: string;
};

type FetchProcessingStatusProps = {
  userId: string;
  requestId: string;
  signal: AbortSignal;
  onMessage: (data: StatusEventResponse) => void;
  onOpen: (response: Response) => void;
  onError: (error: Error) => void;
  onClose: () => void;
};

const LOGGER_TAG = 'FETCH_STATUS';

export const fetchProcessingStatus = async ({
  userId,
  requestId,
  signal,
  onMessage,
  onOpen,
  onError,
  onClose,
}: FetchProcessingStatusProps) => {
  // INFO: The same id as the question's POST, so the stream carries that question's progress and no other
  const url = `${import.meta.env.VITE_BASE_BACKEND_URL}/events/${userId}?request_id=${requestId}`;
  const token = accessToken.getAccessToken() as AccessToken;
  await fetchEventSource(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'text/event-stream',
    },
    // INFO: Without this the browser drops the ALB cookie on this cross-origin request, so the
    // stream can land on a different backend task than the question and receives no progress.
    credentials: 'include',
    signal,
    async onopen(response) {
      if (response.ok) {
        onOpen(response);
      } else {
        onError(new Error('Failed to connect'));
      }
      return Promise.resolve();
    },
    onmessage(event) {
      try {
        const data: StatusEventResponse = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        Logger.trace(LOGGER_TAG, 'Error parsing event data:', error);
        onError(error as Error);
      }
    },
    onerror(err) {
      Logger.error(LOGGER_TAG, 'Error fetching processing status:', err);
      onError(err);
      // INFO: Throwing the error prevents a retry
      throw err;
    },
    onclose() {
      if (onClose) {
        onClose();
      }
    },
  });
};
